import React, { useState } from 'react';
import { Delete, Check, X, Globe, ArrowBigUp } from 'lucide-react';

/**
 * On-screen keyboard for touch-only POS screens (most stores run touch monitors
 * with no physical keyboard). Renders as a fixed bottom sheet so it never
 * stretches or reflows the modal that opened it.
 *
 * mode 'numeric' → compact numpad (ΑΦΜ, Τ.Κ., κωδ. υποκαταστήματος, ποσά).
 * mode 'text'    → full QWERTY with Greek/Latin + shift (uppercase default —
 *                  AADE registry data is uppercase).
 */
interface TouchKeyboardProps {
  mode: 'numeric' | 'text';
  label?: string;
  value: string;
  onChange: (next: string) => void;
  onDone: () => void;
  maxLength?: number;
  /** numeric: υποδιαστολή «,» στη θέση του καθαρισμού (π.χ. 0,537 κιλά). */
  decimal?: boolean;
}

const GREEK_ROWS = [
  [';', 'ς', 'Ε', 'Ρ', 'Τ', 'Υ', 'Θ', 'Ι', 'Ο', 'Π'],
  ['Α', 'Σ', 'Δ', 'Φ', 'Γ', 'Η', 'Ξ', 'Κ', 'Λ'],
  ['Ζ', 'Χ', 'Ψ', 'Ω', 'Β', 'Ν', 'Μ'],
];
const LATIN_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];
const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const TouchKeyboard: React.FC<TouchKeyboardProps> = ({ mode, label, value, onChange, onDone, maxLength, decimal }) => {
  const [greek, setGreek] = useState(true);
  const [caps, setCaps] = useState(true);

  const append = (ch: string) => {
    if (maxLength && value.length >= maxLength) return;
    onChange(value + (caps ? ch.toUpperCase() : ch.toLowerCase()));
  };
  const backspace = () => onChange(value.slice(0, -1));
  const clear = () => onChange('');

  const key = 'h-12 min-w-[2.4rem] flex-1 rounded-lg bg-surface-2 text-t1 text-lg font-medium ' +
    'active:bg-cyan-600 select-none touch-manipulation border border-line-strong';

  return (
    <div className="cashier-keyboard fixed inset-x-0 bottom-0 z-[70]">
      <div className={`mx-auto ${mode === 'numeric' ? 'max-w-sm' : 'max-w-3xl'} bg-surface-0/98 border border-line-strong rounded-t-2xl shadow-2xl p-3 backdrop-blur`}>
        {/* Current value strip */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 bg-surface-2 dark:bg-surface-1 border border-line-strong rounded-lg px-3 py-2 min-h-[2.6rem]">
            {label && <span className="text-[10px] uppercase tracking-wide text-t3 dark:text-t4 block leading-none mb-0.5">{label}</span>}
            <span className="text-t1 text-lg font-mono break-all leading-tight">{value || <span className="text-t4">…</span>}</span>
          </div>
          <button type="button" onClick={onDone} className="cashier-primary h-12 px-5 rounded-lg bg-green-600 active:bg-green-700 text-white font-bold flex items-center gap-1">
            <Check className="h-5 w-5" /> OK
          </button>
        </div>

        {mode === 'numeric' ? (
          <div className="grid grid-cols-3 gap-2">
            {['1','2','3','4','5','6','7','8','9'].map(d => (
              <button type="button" key={d} className={`${key} h-14 text-2xl`} onClick={() => append(d)}>{d}</button>
            ))}
            {decimal ? (
              <button type="button" className={`${key} h-14 text-2xl`} onClick={() => append(',')}>,</button>
            ) : (
              <button type="button" className={`${key} h-14 bg-surface-1 text-t2`} onClick={clear}><X className="h-5 w-5 mx-auto" /></button>
            )}
            <button type="button" className={`${key} h-14 text-2xl`} onClick={() => append('0')}>0</button>
            <button type="button" className={`${key} h-14 bg-surface-1 text-t2`} onClick={backspace}><Delete className="h-5 w-5 mx-auto" /></button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              {DIGITS.map(d => (
                <button type="button" key={d} className={key} onClick={() => append(d)}>{d}</button>
              ))}
            </div>
            {(greek ? GREEK_ROWS : LATIN_ROWS).map((row, i) => (
              <div key={i} className="flex gap-1.5 justify-center">
                {i === 2 && (
                  <button type="button" aria-label="Caps Lock" aria-pressed={caps} className={`cashier-choice ${key} flex-none px-3 ${caps ? 'bg-cyan-700' : 'bg-surface-1'}`} onClick={() => setCaps(c => !c)}>
                    <ArrowBigUp className="h-5 w-5 mx-auto" />
                  </button>
                )}
                {row.map(ch => (
                  <button type="button" key={ch} className={key} onClick={() => append(ch)}>
                    {caps ? ch.toUpperCase() : ch.toLowerCase()}
                  </button>
                ))}
                {i === 2 && (
                  <button type="button" className={`${key} flex-none px-4 bg-surface-1 text-t2`} onClick={backspace}>
                    <Delete className="h-5 w-5 mx-auto" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex gap-1.5">
              <button type="button" className={`${key} flex-none px-3 bg-surface-1 text-t2 text-sm`} onClick={() => setGreek(g => !g)}>
                <Globe className="h-4 w-4 inline mr-1" />{greek ? 'EN' : 'ΕΛ'}
              </button>
              <button type="button" className={key} onClick={() => append('-')}>-</button>
              <button type="button" className={`${key} flex-[4]`} onClick={() => append(' ')}>␣</button>
              <button type="button" className={key} onClick={() => append('@')}>@</button>
              <button type="button" className={key} onClick={() => append('.')}>.</button>
              <button type="button" className={`${key} flex-none px-4 bg-surface-1 text-t2 text-sm`} onClick={clear}>CLR</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TouchKeyboard;
